import React, { type ComponentProps } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { StatusTagDropdown, useDashboardPermissions } from '@components'
import { Banner, Toasts } from '@fourthwall/components'
import { reverse, routing } from '@utils/routing'

import { useOfferQuery } from '../hooks'
import { useStatusMutation } from '../hooks/useStatusMutation'

type ProductStatusTagDropdownContainerProps = {
  size?: ComponentProps<typeof StatusTagDropdown>['size']
}

export const ProductStatusTagDropdownContainer = ({
  size,
}: ProductStatusTagDropdownContainerProps) => {
  const { productId = '' } = useParams<'productId'>()
  const navigate = useNavigate()
  const offerId = productId
  const { offerQuery, setOfferData } = useOfferQuery([offerId])
  const { statusMutation } = useStatusMutation()
  const { hasPermission, hasRole } = useDashboardPermissions()

  if (!offerQuery.isSuccess) return null

  const { state, isSettingCostsRequired } = offerQuery.data
  const { status, available } = state
  const hasParentOffer = !!offerQuery.data.parentOffer

  const renderMissingCost = () => {
    if (!isSettingCostsRequired) return

    if (!hasRole('ROLE_COSTS_WRITE')) return

    return (
      <div style={{ marginBottom: 16 }}>
        <Banner appearance="warning">
          Product is missing costs.{' '}
          <button
            type="button"
            onClick={() => {
              navigate(reverse(routing.products.all.product.costs, { productId: offerId }))
            }}
          >
            Set it up
          </button>
        </Banner>
      </div>
    )
  }

  const handleSubmit: ComponentProps<typeof StatusTagDropdown>['onSubmit'] = async (values) => {
    const { status } = values

    // StatusTagDropdown component still handles too many different statuses
    if (status === 'SCHEDULE') return

    await statusMutation.mutateAsync(
      { offerId, params: { status } },
      {
        onSuccess: (data) => {
          // TODO: decodeHtmlEntities
          setOfferData(data)
        },
        onError: (error) => {
          Toasts.notify(error.message, { type: 'error' })
        },
      },
    )
  }

  return (
    <StatusTagDropdown
      headerContent={renderMissingCost()}
      status={status}
      isSoldOut={!available}
      disabled={!hasPermission('products.edit.status') || status === 'ARCHIVED' || hasParentOffer}
      size={size}
      onSubmit={handleSubmit}
    />
  )
}
